/**
 * Headless mic capture for the Kid console.
 *
 * The forked demo drove mic capture from <ControlTray/> (a visible button bar).
 * The kiosk has NO chrome — the glyph is the only control — so the capture lives
 * here instead: while the Live session is connected, stream 16kHz PCM from the
 * microphone to Gemini. Lifted verbatim from ControlTray's capture effect so the
 * audio path is identical; only the UI is gone.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AudioRecorder } from "../lib/audio-recorder";
import { GenAILiveClient } from "../lib/genai-live-client";

export function useMicStream(client: GenAILiveClient, connected: boolean, muted = false) {
  const [audioRecorder] = useState(() => new AudioRecorder());
  const [inVolume, setInVolume] = useState(0); // mic input amplitude (0..1)
  const [micError, setMicError] = useState<string | null>(null); // surfaced on the status bar — a silent dead mic is undebuggable on a kiosk

  // The recorder emits volume ~40Hz; gate to ~20Hz so the mic meter doesn't
  // re-render its consumers twice as often as needed (matters on the tablet).
  const lastTs = useRef(0);
  const keepAlive = useRef<{ iv?: number; hb?: number; ctx?: AudioContext; onState?: () => void; onVis?: () => void }>({});
  const onVolume = useCallback((v: number) => {
    const now = performance.now();
    if (now - lastTs.current < 45 && v > 0.001) return;
    lastTs.current = now;
    setInVolume(v);
  }, []);

  useEffect(() => {
    // TEMP mic-capture telemetry — beacons the real device mic state to the server
    // so a locked/dead mic is confirmable off-device (kiosk has no devtools).
    const beacon = (o: any) => {
      try {
        fetch("/api/child/mic_diag", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(o),
        }).catch(() => {});
      } catch {
        /* ignore */
      }
    };
    let dataEvents = 0;
    const onData = (base64: string) => {
      dataEvents++;
      client.sendRealtimeInput([{ mimeType: "audio/pcm;rate=16000", data: base64 }]);
    };
    if (connected && !muted) {
      setMicError(null);
      beacon({ phase: "start-attempt", hasGUM: !!navigator?.mediaDevices?.getUserMedia });
      audioRecorder
        .on("data", onData)
        .on("volume", onVolume)
        .start()
        .then(() => {
          const rec = audioRecorder as any;
          const tr = rec.stream?.getAudioTracks?.()[0];
          beacon({
            phase: "started",
            track: tr ? { readyState: tr.readyState, muted: tr.muted, enabled: tr.enabled, label: tr.label } : null,
            ctx: rec.audioContext?.state,
            sampleRate: rec.audioContext?.sampleRate,
          });
          setTimeout(() => {
            const tr2 = rec.stream?.getAudioTracks?.()[0];
            beacon({
              phase: "after3s",
              dataEvents,
              ctx: rec.audioContext?.state,
              trackMuted: tr2?.muted,
              trackState: tr2?.readyState,
            });
          }, 3000);
          // KEEPALIVE + continuous instrumentation. The mic runs on its OWN AudioContext,
          // separate from the output ("audio-out") that wake() resumes. On the tablet it
          // suspends a few seconds after start -> mic frames stop -> barge-in dies until a
          // tap gesture resumes it. Resume it ourselves on a watchdog so no tap is ever
          // needed, and beacon ctx state + frame flow every 5s so the suspend is visible.
          const micCtx = rec.audioContext as AudioContext | undefined;
          if (micCtx) {
            keepAlive.current.ctx = micCtx;
            const resumeIfSuspended = () => {
              if (micCtx.state === "suspended") {
                beacon({ phase: "ctx-resume-attempt", from: micCtx.state });
                micCtx
                  .resume()
                  .then(() => beacon({ phase: "ctx-resumed", to: micCtx.state }))
                  .catch((e: any) => beacon({ phase: "ctx-resume-fail", err: String(e?.message || e) }));
              }
            };
            const onState = () => beacon({ phase: "ctx-statechange", state: micCtx.state });
            micCtx.addEventListener?.("statechange", onState);
            keepAlive.current.onState = onState;
            const onVis = () => resumeIfSuspended();
            document.addEventListener("visibilitychange", onVis);
            window.addEventListener("focus", onVis);
            keepAlive.current.onVis = onVis;
            resumeIfSuspended(); // in case it suspended between start and here
            keepAlive.current.iv = window.setInterval(resumeIfSuspended, 1500);
            let lastCount = 0;
            keepAlive.current.hb = window.setInterval(() => {
              const tr3 = rec.stream?.getAudioTracks?.()[0];
              beacon({
                phase: "heartbeat",
                ctx: micCtx.state,
                framesSince: dataEvents - lastCount,
                trackMuted: tr3?.muted,
                trackState: tr3?.readyState,
              });
              lastCount = dataEvents;
            }, 5000);
          }
        })
        .catch((e: any) => {
          setMicError(e?.name || e?.message || String(e));
          beacon({ phase: "error", name: e?.name || null, message: String(e?.message || e) });
        });
    } else {
      audioRecorder.stop();
      setInVolume(0); // don't freeze the last mic level on the glyph while muted
    }
    return () => {
      audioRecorder.off("data", onData).off("volume", onVolume);
      audioRecorder.stop(); // unmount must never leave the child's mic hot
      const ka = keepAlive.current;
      if (ka.iv) clearInterval(ka.iv);
      if (ka.hb) clearInterval(ka.hb);
      if (ka.ctx && ka.onState) ka.ctx.removeEventListener?.("statechange", ka.onState);
      if (ka.onVis) {
        document.removeEventListener("visibilitychange", ka.onVis);
        window.removeEventListener("focus", ka.onVis);
      }
      keepAlive.current = {};
    };
  }, [connected, client, muted, audioRecorder, onVolume]);

  // Release the mic when the PAGE is torn down. A Fully Kiosk reload (loadStartURL)
  // navigates the WebView but does NOT reliably run React unmount cleanup, so the
  // getUserMedia tracks are never stopped — the WebView keeps the mic locked and
  // the NEXT page load comes up with a dead mic (session connects, no error, no
  // audio). That is the "force-stop to fix it" gremlin, and it is a real leak, not
  // an unavoidable flake: stop the recorder hard on pagehide so the mic is handed
  // back cleanly and the next load acquires a live stream.
  useEffect(() => {
    const release = () => {
      try {
        audioRecorder.stop();
      } catch {
        /* nothing to release */
      }
    };
    window.addEventListener("pagehide", release);
    window.addEventListener("beforeunload", release);
    return () => {
      window.removeEventListener("pagehide", release);
      window.removeEventListener("beforeunload", release);
    };
  }, [audioRecorder]);

  return { inVolume, micError };
}
